import moment from 'moment'

import { deriveMediaDockAnswerTitle } from 'components/shared/MediaDock/mediaDockTrackTitles'

import { audioAttachmentMediaKey, videoPayloadMediaKey } from './Answer/payloadMediaIdentity'

const mediaOfType = (items, prefix) =>
    items.find(
        file =>
            file &&
            typeof file === 'object' &&
            file.url &&
            String(file.content_type || file.mime || '').startsWith(prefix),
    ) || null

// Match Answer#radio_media_track: audio first (uploads, then generated), and
// native video as the fallback (the MP4 recording, then uploads, then
// generated). The result is the source entry plus its `kind` and, for video,
// the poster that stands in as cover art.
export function radioSongMedia(song) {
    const files = song?.file_urls || song?.files || []
    const outputs = (song?.ai_runs || []).flatMap(run => run?.output_urls || [])

    const audio = mediaOfType([...files, ...outputs], 'audio/')
    if (audio) return { ...audio, kind: 'audio' }

    if (song?.mp4_recording_url) {
        return {
            url: song.mp4_recording_url,
            attachment_id: song.mp4_recording_attachment_id || null,
            blob_id: song.mp4_recording_blob_id || null,
            content_type: 'video/mp4',
            kind: 'video',
            poster_url: song.recording_thumbnail_url || null,
        }
    }

    const video = mediaOfType([...files, ...outputs], 'video/')
    if (!video) return null
    return {
        ...video,
        kind: 'video',
        poster_url: video.poster_url || video.preview_url || video.thumbnail_url || null,
    }
}

// The station's payload carries the song's media; shaped like the song's
// post, it lets a card draw the song (same media and dock key) before the post
// itself loads. Only the media fields: the post replaces it once cached.
export function radioSongStandIn(track) {
    if (!track?.answerId || !track?.src) return null
    if (track.kind === 'video') {
        return {
            id: track.answerId,
            radio_stand_in: true,
            mp4_recording_url: track.src,
            mp4_recording_attachment_id: track.attachmentId || null,
            mp4_recording_blob_id: track.blobId || null,
            recording_thumbnail_url: track.posterSrc || null,
        }
    }
    return {
        id: track.answerId,
        radio_stand_in: true,
        answer_image_url: track.posterSrc || null,
        file_urls: [
            {
                url: track.src,
                content_type: track.contentType || 'audio/mpeg',
                attachment_id: track.attachmentId || null,
                blob_id: track.blobId || null,
            },
        ],
    }
}

export const radioSongMediaKey = media => {
    const identity = {
        url: media.url,
        attachmentId: media.attachment_id || media.id,
        blobId: media.blob_id,
    }
    return media.kind === 'video'
        ? videoPayloadMediaKey(identity)
        : audioAttachmentMediaKey(identity)
}

export const radioPlayTitle = answer =>
    String(answer?.content || '')
        .replace(/^(?:📻\s*Now playing on Treechat (?:Radio|Music):|▶️\s*Now playing:)\s*/, '')
        .replace(/ — play #\d+.*$/, '')
        .trim() || 'this song'

// The station post names the song by its file; the dock names it by the
// song post's text. The card shows the dock's name, and the file name only
// when the post has no text of its own.
export const radioSongDisplayTitle = (song, fallback) =>
    deriveMediaDockAnswerTitle(song) || fallback

export function radioAttributionSummary(answer) {
    const content = String(answer?.content || '')
    const match = / (?:is|are) playing ([\s\S]*)/.exec(content)
    const others = Math.max(
        0,
        Number(answer?.action_tags?.media_announcement?.listener_count || 1) - 1,
    )
    return {
        title: match?.[1]?.trim() || 'this song',
        audience:
            others > 0
                ? ` and ${others} ${others === 1 ? 'other' : 'others'} are playing `
                : ' is playing ',
    }
}

// The thread a radio card opens for its song. A reply lives in its quest; a
// top-level post (most uploaded songs) has no quest of its own, so its thread
// is the child quest hanging off it.
export function radioSongThreadQuestId(song) {
    const questId =
        song?.quest_id ||
        song?.uptree_quest_id ||
        song?.downtree_quest_id ||
        song?.child_quests?.[0]?.id
    return questId ? String(questId) : null
}

// Quoted cards do not mount the interactive radio UI; keep their text visible.
export function radioCardReplacesBody(answer, isEmbed) {
    if (isEmbed) return false
    const tags = answer?.action_tags
    return Boolean(
        tags?.radio_play?.song_answer_id ||
            tags?.media_announcement?.song_answer_id ||
            tags?.radio_attribution?.song_answer_id ||
            // The chart: three sections, each a list of contenders. Any one
            // with a row means the card carries the reply and the text yields.
            (Array.isArray(tags?.radio_race?.sections) &&
                tags.radio_race.sections.some(
                    section => Array.isArray(section?.contenders) && section.contenders.length > 0,
                )),
    )
}

const positiveNumber = value => {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? number : null
}

export function radioPlayIndexRelation(stationPlayIndex: any, cardPlayIndex: any) {
    if (stationPlayIndex == null || cardPlayIndex == null) return 'unknown'
    const station = Number(stationPlayIndex)
    const card = Number(cardPlayIndex)
    if (!Number.isFinite(station) || !Number.isFinite(card)) return 'unknown'
    if (station === card) return 'same'
    return station < card ? 'before' : 'after'
}

// The station's own clock. Anchored on the latest broadcast sample and the
// client time it ARRIVED, so it ticks from elapsed local time and never has to
// reconcile server and client clocks; before a sample arrives it falls back to
// the play's started_at from the card's tag. Clamped to the track when its
// length is known (the sample's, else the file's). Null when nothing anchors
// it.
export function stationPosition(
    {
        startedAt,
        sample,
        fallbackDuration,
    }: { startedAt?: any; sample?: any; fallbackDuration?: any },
    clientNowMs = Date.now(),
) {
    const duration = positiveNumber(sample?.duration) ?? positiveNumber(fallbackDuration)
    let position: number | null = null
    if (
        sample &&
        Number.isFinite(Number(sample.position)) &&
        Number.isFinite(Number(sample.receivedAt))
    ) {
        position =
            Number(sample.position) + Math.max(0, clientNowMs - Number(sample.receivedAt)) / 1000
    } else if (positiveNumber(startedAt)) {
        position = clientNowMs / 1000 - Number(startedAt)
    }
    if (position == null) return null

    position = Math.max(0, position)
    if (duration) position = Math.min(position, duration)
    return {
        position,
        duration,
        progress: duration ? Math.min(1, position / duration) : null,
    }
}

// The song thread's own airplay notice (Radio::AirplayNoticeService): one
// post per song, rewritten and moved to the end of the thread on every
// listened play. Its tag carries the count and the latest slot's window.
export function radioAirplayMetaFromAnswer(answer) {
    const meta = answer?.action_tags?.radio_airplay
    return meta?.song_answer_id ? meta : null
}

// "On air now" while the latest slot is live; otherwise how long ago the song
// last aired, from that slot's own start — the post's byline keeps the date
// the notice was first made, which is not when it last played.
export function radioAirplayStatusLabel(meta, isLive, nowMs = Date.now()) {
    if (!meta) return ''
    if (isLive) return 'On air now'
    const startedAt = Number(meta.started_at)
    if (!Number.isFinite(startedAt) || startedAt <= 0) return ''
    return `Last on air ${moment(startedAt * 1000).from(nowMs)}`
}
